import CategoryLink from '@jetshop/ui/CategoryLink';
import UIMenuContainer from '@jetshop/ui/Menu/MenuContainer';
import React from 'react';
import { styled } from 'linaria/react';
import { useIntl } from '@jetshop/intl';
import SubMenuWrapper from './SubMenuWrapper';
import { theme } from '../../../Theme';
import { cleanTextString } from '../../../CategoryPage/CategoryHeader';

const Wrapper = styled('div')`
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
`;

const MainMenuItem = styled('li')`
  a,
  button {
    font-size: 15px;
    padding: 0px 0.75rem 18px;
    display: inline-block;
    color: ${theme.colors.primary};
    text-decoration: none;
    position: relative;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;

    &:after {
      content: '';
      height: 3px;
      background: ${theme.colors.accent};
      width: 100%;
      position: absolute;
      bottom: 10px;
      left: 1rem;
      right: 1rem;
      width: auto;
      opacity: 0;
      transition: all, 0.2s ease;
    }

    &:hover,
    &.active {
      color: ${theme.colors.accent};
      text-decoration: none !important;
      &:after {
        opacity: 1;
      }
    }

    &:focus {
      outline: none;
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &:focus-visible {
      outline: none;
    }

    ${theme.below.xl} {
      padding: 0px 0.5rem 18px;
    }
  }
`;

const CategoryMenuContainer = ({ queryData }) => {
  const translate = useIntl();

  // Handle keyboard events for menu items
  const handleKeyDown = (event, cat, props) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleMenuClick(cat, props);
        break;
      case 'Escape':
        event.preventDefault();
        props.clearActiveCategories();
        break;
      case 'ArrowRight':
        event.preventDefault();
        // Focus next menu item
        const nextItem =
          event.target.parentElement.nextElementSibling?.querySelector(
            'a, button'
          );
        if (nextItem) nextItem.focus();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        // Focus previous menu item
        const prevItem =
          event.target.parentElement.previousElementSibling?.querySelector(
            'a, button'
          );
        if (prevItem) prevItem.focus();
        break;
      default:
        console.info(`Keydown event ${event.key} not handled`);
        break;
    }
  };

  // Handle menu item click/tap
  const handleMenuClick = (cat, props) => {
    if (cat.id !== 155 && cat.id !== 540) {
      props.setActiveCategory(cat);
    } else {
      props.clearActiveCategories();
    }
    if (props?.isActiveCategory(cat)) {
      props.clearActiveCategories();
    }
  };

  // Handle link click
  const handleLinkClick = (e, cat, props) => {
    if (
      cat.subcategories &&
      cat.subcategories.length > 0 &&
      cat.id !== 155 &&
      cat.id !== 540
    ) {
      e.preventDefault();
    } else {
      props.clearActiveCategories();
    }
  };

  return (
    <UIMenuContainer>
      {props => (
        <Wrapper
          onMouseLeave={props.clearActiveCategories}
          role="navigation"
          aria-label={translate('main category menu')}
        >
          <ul
            style={{ paddingTop: '20px' }}
            role="menubar"
            aria-label={translate('category navigation')}
          >
            {queryData && queryData.categories
              ? queryData.categories.map((cat, index) =>
                  cat.id === 155 ? null : (
                    <MainMenuItem key={cat.id} role="none">
                      {cat.subcategories &&
                      cat.subcategories.length > 0 &&
                      cat.id !== 155 &&
                      cat.id !== 540 ? (
                        // This is a menu button that opens submenu
                        <button
                          type="button"
                          role="menuitem"
                          aria-haspopup="true"
                          aria-expanded={props?.isActiveCategory(cat) || false}
                          aria-label={`${cleanTextString(
                            cat.name
                          )} - ${translate('has_submenu')}`}
                          onClick={() => handleMenuClick(cat, props)}
                          onKeyDown={e => handleKeyDown(e, cat, props)}
                          className={
                            (
                              (
                                props.activeCategories
                                  ? props.activeCategories.length > 0
                                  : null
                              )
                                ? props.activeCategories[0].id === cat.id
                                : null
                            )
                              ? 'active'
                              : null
                          }
                        >
                          {cleanTextString(cat.name)}
                        </button>
                      ) : (
                        // This is a direct link
                        <CategoryLink
                          role="menuitem"
                          aria-label={`${translate(
                            'navigate to'
                          )} ${cleanTextString(cat.name)}`}
                          onClick={e => handleLinkClick(e, cat, props)}
                          onKeyDown={e => handleKeyDown(e, cat, props)}
                          category={cat}
                          className={
                            (
                              (
                                props.activeCategories
                                  ? props.activeCategories.length > 0
                                  : null
                              )
                                ? props.activeCategories[0].id === cat.id
                                : null
                            )
                              ? 'active'
                              : null
                          }
                        >
                          {cleanTextString(cat.name)}
                        </CategoryLink>
                      )}
                    </MainMenuItem>
                  )
                )
              : null}
          </ul>

          <SubMenuWrapper
            pose={
              props.activeCategories.length > 0 &&
              props.activeCategories[0].subcategories.length > 0
                ? 'open'
                : 'closed'
            }
            activeCategory={props.activeCategories[0]}
            closeNav={props.clearActiveCategories}
          />
        </Wrapper>
      )}
    </UIMenuContainer>
  );
};

export default CategoryMenuContainer;
